// src/pages/admin/CourseLectures/lectures/FSJLecture01Introduction.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-4 rounded-2xl border overflow-hidden shadow-sm ${
        isOpen ? "border-[#009fe3]/50" : "border-gray-200"
      } bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${
          isOpen ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wide transition-colors ${
            isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"
          }`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-base font-bold ${
            isOpen ? "text-[#0369a1]" : "text-gray-800"
          }`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-3 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Comparison Table Row ──────────────────────────────────────────
const CompareRow: React.FC<{
  label: string;
  fsj: string;
  bfd: string;
}> = ({ label, fsj, bfd }) => (
  <div className="grid grid-cols-3 gap-2 text-sm">
    <div className="bg-gray-50 rounded-lg px-3 py-2 font-semibold text-gray-600">
      {label}
    </div>
    <div className="bg-blue-50 rounded-lg px-3 py-2 text-blue-800">{fsj}</div>
    <div className="bg-teal-50 rounded-lg px-3 py-2 text-teal-800">{bfd}</div>
  </div>
);

// ── Eligibility Row ───────────────────────────────────────────────
const EligibilityRow: React.FC<{
  criteria: string;
  requirement: string;
  pass: boolean;
}> = ({ criteria, requirement, pass }) => (
  <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-4 py-3">
    <span
      className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
        pass ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
      }`}
    >
      {pass ? "✓" : "✗"}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold text-gray-700">{criteria}</p>
      <p className="text-xs text-gray-500 mt-0.5">{requirement}</p>
    </div>
  </div>
);

// ── Pro Con Card ──────────────────────────────────────────────────
const ProConCard: React.FC<{
  icon: string;
  title: string;
  items: string[];
  color: string;
}> = ({ icon, title, items, color }) => (
  <div className={`rounded-2xl border p-4 ${color}`}>
    <p className="text-sm font-bold mb-3 flex items-center gap-2">
      <span>{icon}</span> {title}
    </p>
    <div className="space-y-2">
      {items.map((item, i) => (
        <div
          key={i}
          className="flex items-start gap-2 bg-white/60 rounded-xl px-3 py-2"
        >
          <span className="flex-shrink-0 mt-0.5 font-bold">›</span>
          <p className="text-sm opacity-90">{item}</p>
        </div>
      ))}
    </div>
  </div>
);



// ── Main Component ────────────────────────────────────────────────
const FSJLecture01Introduction: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div className="max-w-4xl mx-auto">
      {/* Course Header Banner */}
      <div className="bg-gradient-to-r from-[#009fe3] to-[#0369a1] rounded-2xl px-6 py-5 mb-5">
        <p className="text-xs font-bold uppercase tracking-widest text-white opacity-70 mb-1">
          FSJ / BFD Preparatory Course
        </p>
        <p className="text-xl font-extrabold text-white">
          Session 01 — What is FSJ / BFD? Eligibility, Goals & Language
        </p>
        <p className="text-sm text-white opacity-80 mt-1">
          Designed by Guideline2Germany
        </p>
      </div>

      <AgendaCard
        items={[
          {
            num: "1",
            label: "What is FSJ & BFD? — Overview & Key Differences",
          },
          { num: "2", label: "Eligibility Check — Do I Qualify?" },
          { num: "3", label: "Advantages — Why FSJ / BFD?" },
          { num: "4", label: "Challenges — Honest Expectations" },
          { num: "5", label: "The Final Goal — What Comes After?" },
          { num: "6", label: "Why German? — A2 Minimum, B1 Recommended" },
        ]}
      />

      {/* 01 — What is FSJ / BFD */}
      <Accordion
        id="01"
        num="SECTION 01"
        icon="🤝"
        title="What is FSJ & BFD? — Overview & Key Differences"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          {/* FSJ Card */}
          <div className="rounded-2xl border-2 border-blue-300 bg-blue-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">🌱</span>
              <div>
                <p className="text-base font-extrabold text-blue-800">FSJ</p>
                <p className="text-xs text-blue-600 font-semibold">
                  Freiwilliges Soziales Jahr
                </p>
              </div>
            </div>
            <p className="text-sm text-blue-700 mb-3">
              <strong>Voluntary Social Year</strong> — a structured full-time
              volunteer service in a social organization. You work, learn, and
              contribute to society.
            </p>
            <div className="space-y-1.5">
              {[
                "Hospitals & nursing homes",
                "Kindergartens & schools",
                "Disability & social services",
                "Sports & cultural institutions",
              ].map((s, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-white/70 rounded-lg px-3 py-1.5"
                >
                  <span className="text-blue-400 font-bold flex-shrink-0">
                    ›
                  </span>
                  <p className="text-sm text-blue-800">{s}</p>
                </div>
              ))}
            </div>
          </div>

          {/* BFD Card */}
          <div className="rounded-2xl border-2 border-teal-300 bg-teal-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">🌍</span>
              <div>
                <p className="text-base font-extrabold text-teal-800">BFD</p>
                <p className="text-xs text-teal-600 font-semibold">
                  Bundesfreiwilligendienst
                </p>
              </div>
            </div>
            <p className="text-sm text-teal-700 mb-3">
              <strong>Federal Volunteer Service</strong> — a broader national
              program covering more sectors. Created in 2011 when civilian
              service (Zivildienst) was abolished.
            </p>
            <div className="space-y-1.5">
              {[
                "All FSJ sectors + more",
                "Environmental & nature conservation",
                "Emergency & disaster services",
                "No strict upper age limit",
              ].map((s, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-white/70 rounded-lg px-3 py-1.5"
                >
                  <span className="text-teal-400 font-bold flex-shrink-0">
                    ›
                  </span>
                  <p className="text-sm text-teal-800">{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
          FSJ vs. BFD — Side by Side
        </p>
        <div className="rounded-2xl border border-gray-200 overflow-hidden mb-5">
          {/* Header */}
          <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-gray-100">
            <p className="text-sm font-bold text-gray-500">Criteria</p>
            <p className="text-sm font-bold text-blue-700">FSJ</p>
            <p className="text-sm font-bold text-teal-700">BFD</p>
          </div>
          <div className="p-3 space-y-2">
            <CompareRow
              label="Age Limit"
              fsj="16 – 27 years"
              bfd="16+ (no upper limit)"
            />
            <CompareRow
              label="Duration"
              fsj="6 – 18 months"
              bfd="6 – 18 months"
            />
            <CompareRow
              label="Pocket Money"
              fsj="~400 – 700 €/month"
              bfd="~400 – 700 €/month"
            />
            <CompareRow
              label="Accommodation"
              fsj="Often provided by organization"
              bfd="Often provided by organization"
            />
            <CompareRow
              label="Health Insurance"
              fsj="Fully covered"
              bfd="Fully covered"
            />
            <CompareRow
              label="Mandatory Seminars"
              fsj="25 days / year"
              bfd="25 days / year"
            />
            <CompareRow
              label="Visa Type"
              fsj="National Visa (FSJ)"
              bfd="National Visa (BFD)"
            />
            <CompareRow
              label="Organizing Body"
              fsj="NGOs, churches, welfare orgs"
              bfd="Federal government (BAMF)"
            />
          </div>
        </div>

        <InfoBox variant="info" icon="🇩🇪" title="Legally Recognized Programs">
          Both FSJ (§ 3 JFDG) and BFD (§ 1 BFDG) are regulated by German federal
          law. They are <strong>official legal pathways</strong> to live, work,
          and integrate in Germany — recognized by the German government and
          embassies worldwide.
        </InfoBox>
      </Accordion>

      {/* 02 — Eligibility */}
      <Accordion
        id="02"
        num="SECTION 02"
        icon="✅"
        title="Eligibility Check — Do I Qualify?"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-5">
          Check every requirement honestly. All boxes must be ticked before
          applying to any FSJ or BFD placement.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          {/* FSJ Eligibility */}
          <div>
            <p className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-3">
              🌱 FSJ Requirements
            </p>
            <div className="space-y-2">
              <EligibilityRow
                criteria="Age"
                requirement="Between 16 and 27 years old at start date"
                pass
              />
              <EligibilityRow
                criteria="School Completion"
                requirement="Completed school education (SSC/HSC equivalent)"
                pass
              />
              <EligibilityRow
                criteria="German Language"
                requirement="Minimum A2 level — B1 strongly recommended"
                pass
              />
              <EligibilityRow
                criteria="Health"
                requirement="No serious conditions preventing service work"
                pass
              />
              <EligibilityRow
                criteria="Commitment"
                requirement="Willing to work full-time (up to 40 hours/week)"
                pass
              />
              <EligibilityRow
                criteria="Nationality"
                requirement="National Visa (FSJ) from German embassy required"
                pass
              />
            </div>
          </div>

          {/* BFD Eligibility */}
          <div>
            <p className="text-sm font-bold text-teal-700 uppercase tracking-wide mb-3">
              🌍 BFD Requirements
            </p>
            <div className="space-y-2">
              <EligibilityRow
                criteria="Age"
                requirement="16 and above — no upper age limit"
                pass
              />
              <EligibilityRow
                criteria="School Completion"
                requirement="Completed school education (SSC/HSC equivalent)"
                pass
              />
              <EligibilityRow
                criteria="German Language"
                requirement="Minimum A2 level — B1 strongly recommended"
                pass
              />
              <EligibilityRow
                criteria="Health"
                requirement="No serious conditions preventing service work"
                pass
              />
              <EligibilityRow
                criteria="Commitment"
                requirement="Willing to work full-time (up to 40 hours/week)"
                pass
              />
              <EligibilityRow
                criteria="Nationality"
                requirement="National Visa (BFD) from German embassy required"
                pass
              />
            </div>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4">
          <p className="text-base font-bold text-red-800 mb-2">
            ❌ Automatic Disqualification
          </p>
          <div className="space-y-1.5">
            {[
              "Over 27 years old at application time (FSJ only — BFD has no age limit)",
              "No German language knowledge and unwilling to reach A2 before departure",
              "Serious criminal record — background check required by most organizations",
              "Medical conditions that prevent working with vulnerable groups (elderly, children)",
            ].map((item, i) => (
              <p
                key={i}
                className="text-sm text-red-700 flex items-start gap-2"
              >
                <span className="flex-shrink-0 mt-0.5 font-bold">›</span> {item}
              </p>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 03 — Advantages */}
      <Accordion
        id="03"
        num="SECTION 03"
        icon="🌟"
        title="Advantages — Why FSJ / BFD is a Great Opportunity"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-5">
          FSJ and BFD are among the most accessible legal pathways to Germany.
          Here is what you genuinely gain:
        </p>
        <div className="space-y-3">
          {[
            {
              icon: "🛂",
              title: "Legal Residence in Germany",
              desc: "Official national visa — fully government-recognized. No grey areas, no risk. You are in Germany legally from day one.",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              icon: "🎓",
              title: "Professional German Work Experience",
              desc: "Real documented work experience in a recognized German institution — hospitals, schools, social services. This is gold on any German CV.",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              icon: "🚀",
              title: "Direct Career Springboard",
              desc: "After FSJ/BFD: Ausbildung in your service field, university, job visa, or permanent residence pathway becomes far more accessible.",
              color: "bg-teal-50 border-teal-200 text-teal-800",
            },
            {
              icon: "🌍",
              title: "Real Integration — Not Tourism",
              desc: "You live and work inside German society. Language, culture, workplace norms — you absorb everything from the inside.",
              color: "bg-rose-50 border-rose-200 text-rose-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-2xl border p-4 ${item.color}`}>
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-base font-bold mb-1">{item.title}</p>
                  <p className="text-sm opacity-80">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 04 — Challenges */}
      <Accordion
        id="04"
        num="SECTION 04"
        icon="⚠️"
        title="Challenges — Honest Expectations"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mb-5">
          <p className="text-base font-bold text-amber-800 mb-1">
            No sugarcoating — know what you are signing up for.
          </p>
          <p className="text-sm text-amber-700">
            Every challenge below is real. Every challenge below also has a
            solution — and this course prepares you for all of them.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <ProConCard
            icon="😔"
            title="Emotional Challenges"
            color="bg-red-50 border-red-200 text-red-800"
            items={[
              "Homesickness — you are far from family in an unfamiliar country",
              "Culture shock — German social norms differ significantly from Bangladesh",
              "Language frustration — everything around you is in German",
              "Loneliness at the start before building a social circle",
            ]}
          />
          <ProConCard
            icon="⏰"
            title="Practical Challenges"
            color="bg-orange-50 border-orange-200 text-orange-800"
            items={[
              "Full-time commitment — up to 40 hours per week of actual service",
              "Pocket money, not a salary — 400–700 € requires careful budgeting",
              "Emotionally demanding work — hospitals, elderly care, disability services",
              "German bureaucracy — registrations, paperwork, bank accounts",
            ]}
          />
        </div>

        <div className="rounded-2xl border border-purple-200 bg-purple-50 p-4 mb-5">
          <p className="text-sm font-bold text-purple-800 mb-2">
            🏢 The Organization Risk
          </p>
          <p className="text-sm text-purple-700">
            Not every placement organization is the same. Some have better
            support structures, accommodation quality, and mentorship than
            others. This is exactly why{" "}
            <strong>
              the organization selection process is covered in depth
            </strong>{" "}
            in this course.
          </p>
        </div>

        <InfoBox variant="tip" icon="💪" title="Every challenge has a solution">
          Homesickness → community building from day one. Language barrier → A2
          before you leave, B1 by month six. Bad organization → proper research
          and targeted applications. This course prepares you for all of it.
        </InfoBox>
      </Accordion>

      {/* 05 — Final Goal */}
      <Accordion
        id="05"
        num="SECTION 05"
        icon="🚀"
        title="The Final Goal — What Comes After FSJ / BFD?"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-2xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4 mb-5">
          <p className="text-base font-bold text-[#0369a1] mb-1">
            FSJ / BFD is not the destination — it is the launchpad.
          </p>
          <p className="text-sm text-[#0369a1]">
            Every month of service builds German language skills, documented
            work experience, and professional references that open the next
            door.
          </p>
        </div>

        <div className="relative pl-7 space-y-4 mb-5">
          <div className="absolute left-2.5 top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#009fe3] via-purple-400 to-green-500 rounded-full" />
          {[
            {
              step: "Before Departure",
              label: "A2 German + Application",
              desc: "Minimum A2 certificate, matched with a placement organization, national visa obtained",
              color: "bg-blue-50 border-blue-200 text-blue-800",
              dot: "bg-[#009fe3]",
              offset: "0rem",
            },
            {
              step: "Months 1–6",
              label: "Integration & A2 → B1",
              desc: "Settling in, language school attendance, daily German immersion, building your network",
              color: "bg-purple-50 border-purple-200 text-purple-800",
              dot: "bg-purple-400",
              offset: "5.5rem",
            },
            {
              step: "Months 6–12",
              label: "B1 / B2 Level + Work Experience",
              desc: "Fluency builds, performance references from the organization, Ausbildung applications open",
              color: "bg-indigo-50 border-indigo-200 text-indigo-800",
              dot: "bg-indigo-400",
              offset: "11rem",
            },
            {
              step: "After FSJ / BFD",
              label: "Ausbildung / University / Job",
              desc: "Visa conversion possible. Nursing Ausbildung, Erzieher/in, social work degree, or work permit",
              color: "bg-green-50 border-green-200 text-green-800",
              dot: "bg-green-500",
              offset: "16.5rem",
            },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-4">
              <div
                className={`absolute left-1 w-3.5 h-3.5 rounded-full border-2 border-white ${item.dot}`}
                style={{ top: `${i * 6 + 0.5}rem` }}
              />
              <div
                className={`flex-1 rounded-2xl border px-4 py-3 ${item.color}`}
              >
                <p className="text-xs font-bold uppercase tracking-wide opacity-60 mb-0.5">
                  {item.step}
                </p>
                <p className="text-base font-bold">{item.label}</p>
                <p className="text-sm opacity-80 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-bold text-green-800 mb-3">
            ✅ Real outcomes from former FSJ / BFD volunteers:
          </p>
          <div className="space-y-2">
            {[
              "Completed Ausbildung as Pflegefachfrau (nursing professional) directly after FSJ",
              "Accepted into a German university with FSJ experience as a key advantage",
              "Obtained a skilled-worker visa (Fachkräftevisa) after BFD + Ausbildung",
              "Transitioned from BFD to permanent residence after 5 years in Germany",
              "Built German language from A2 to C1 entirely through FSJ service + school",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 bg-white/60 rounded-xl px-3 py-2"
              >
                <span className="text-green-500 font-bold flex-shrink-0 mt-0.5">
                  ✓
                </span>
                <p className="text-sm text-green-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 06 — Language Importance */}
      <Accordion
        id="06"
        num="SECTION 06"
        icon="🇩🇪"
        title="Why German? — A2 Minimum, B1 Recommended"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 mb-5">
          <p className="text-base font-bold text-red-800 mb-1">
            German is not optional — it is the foundation of everything.
          </p>
          <p className="text-sm text-red-700">
            Without A2 German before departure, the visa interview, the
            organization placement, and your daily work life become serious and
            unnecessary obstacles.
          </p>
        </div>

        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
          Why A2 is the minimum — and why B1 is the real target:
        </p>
        <div className="space-y-3 mb-6">
          {[
            {
              icon: "🏛️",
              title: "The German Embassy Checks It",
              desc: "Basic German language skills are assessed or expected during the national visa interview. Coming with zero German significantly increases rejection risk.",
              color: "bg-red-50 border-red-200 text-red-800",
            },
            {
              icon: "🏥",
              title: "Your Workplace Operates in German — Only",
              desc: "Whether it is a hospital, kindergarten, or elderly care home — every instruction, every patient, every colleague communicates in German.",
              color: "bg-orange-50 border-orange-200 text-orange-800",
            },
            {
              icon: "📋",
              title: "Daily Documentation is in German",
              desc: "Care records, shift handovers, incident reports — you must read and write basic German professionally from week one.",
              color: "bg-amber-50 border-amber-200 text-amber-800",
            },
            {
              icon: "📚",
              title: "Seminars Are Conducted in German",
              desc: "All mandatory FSJ/BFD seminars (25 days/year) are in German. A2 lets you follow along. B1 lets you participate and grow.",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              icon: "🔓",
              title: "B1 Unlocks the Next Chapter",
              desc: "Ausbildung applications, university enrollment, and skilled-worker visas all require B1 or higher. Reaching B1 during your service is your most important personal goal.",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-2xl border p-4 ${item.color}`}>
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-base font-bold mb-1">{item.title}</p>
                  <p className="text-sm opacity-80">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
          <p className="text-base font-bold text-green-800 mb-3">
            📍 Where to Learn German in Bangladesh
          </p>
          <div className="space-y-2">
            {[
              {
                name: "Goethe-Institut Dhaka",
                desc: "Most recognized certificate — accepted by the German embassy for visa applications. Best choice for your A2 exam.",
                badge: "Most Recognized",
                badgeColor: "bg-[#009fe3] text-white",
              },
              {
                name: "DAAD Language Programs",
                desc: "German Academic Exchange Service — structured and reliable. Good for motivated learners.",
                badge: "Recommended",
                badgeColor: "bg-green-700 text-white",
              },
              {
                name: "Deutsche Welle — dw.com/learn-german",
                desc: "Free online learning platform — excellent supplement to in-person classes. Not a replacement for the official exam.",
                badge: "Free",
                badgeColor: "bg-amber-500 text-white",
              },
              {
                name: "Neon Lingua / Private Language Schools",
                desc: "Various private options in Dhaka — check for Goethe-affiliated exam centers specifically.",
                badge: "Verify First",
                badgeColor: "bg-gray-500 text-white",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-green-200 px-4 py-3"
              >
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="text-sm font-bold text-green-800">
                    {item.name}
                  </p>
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.badgeColor}`}
                  >
                    {item.badge}
                  </span>
                </div>
                <p className="text-sm text-green-700 opacity-80">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* Summary */}
      <Accordion
        id="summary"
        num="SUMMARY"
        icon="🏁"
        title="Session Summary"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-4 p-5 bg-green-50 border border-green-200 rounded-2xl">
          <span className="text-3xl flex-shrink-0">✅</span>
          <div>
            <p className="text-base font-bold text-green-800 mb-3">
              You now understand the full foundation of the FSJ / BFD program.
            </p>
            <ul className="space-y-2">
              {[
                "FSJ = Freiwilliges Soziales Jahr (16–27 years) — social service focused",
                "BFD = Bundesfreiwilligendienst (27+ years) — broader sectors, no age cap",
                "Both are legal, government-recognized pathways to live and work in Germany",
                "Eligibility: completed school, A2 German, no serious health restrictions",
                "Benefits: pocket money, full insurance, accommodation, work experience, seminars",
                "Challenges are real — emotional, financial, and practical — all have solutions",
                "Final goal: language up to B1/B2, then Ausbildung / university / work visa",
                "A2 is the minimum — B1 before end of service is your personal target",
                "Start at Goethe-Institut Dhaka immediately — this is your first action item",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5 font-bold">›</span>{" "}
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Tasks */}
      <Accordion
        id="tasks"
        num="TASKS"
        icon="📋"
        title="Your Tasks — Session 01"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              <>
                <strong>Eligibility Self-Check</strong> — Confirm your age,
                education, and health status against every FSJ and BFD
                requirement listed in this session
              </>,
              <>
                <strong>FSJ vs. BFD Decision</strong> — Based on your age and
                career interest, write down which program fits you better and
                why. One paragraph is enough.
              </>,
              <>
                <strong>Assess Your German Level</strong> — Are you A1, A2, or a
                complete beginner? Be completely honest about where you
                currently stand.
              </>,
              <>
                <strong>Research Goethe-Institut Dhaka</strong> — Find the next
                available A2 course: start date, duration, cost, and exam
                registration deadline
              </>,
              <>
                Memorize this sentence:{" "}
                <strong>"Ich möchte meinen FSJ in Deutschland machen."</strong>{" "}
                — This is your first German sentence for this course.
              </>,
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default FSJLecture01Introduction;
